<template>
  <div>
    <Stepper
        :tabs="tabs"
        :captions="captions"
        :complete="complete"
        @position="stepChange($event)"
        ref="stepper"
        v-if="!!customer"
        vertical
    >

      <template v-slot:BasicInfo>
        <BasicInfo
            v-model="basicFormFields"
            :customer="customer"
            ref="basicInfoForm">

        </BasicInfo>
      </template>

    </Stepper>
    <div class="text-left py-2">
      <v-btn color="primary" class="mb-2" @click="saveAndGoToFieldSurvey">Submit and fill field survey form</v-btn>
    </div>
    <v-snackbar
        v-model="snackbar"
        :timeout="timeout"
        color="green"
        elevation="24"
    >
      You have successfully onboarded the farmer
    </v-snackbar>

  </div>
</template>

<script>
import BasicInfo from "@/views/partners/eco_bw/views/register/BasicInfo";
import Stepper from "@/components/Stepper";
import StepperValidationSection from "@/views/partners/eco_bw/components/StepperValidationSection";
import { present, ecoBwAnswers } from "@/views/partners/eco_bw/utils";

export default {
  components: {
    BasicInfo,
    Stepper,
    StepperValidationSection
  },
  data () {
    return {
      snackbar: false,
      timeout: 2000,
      valid: false,
      step: 1,
      tabs: [
        'BasicInfo',
      ],
      captions: {
        'BasicInfo': 'Basic Details',
      },
      basicFormFields: {
        firstName: null,
        surname: null,
        msisdn: null,
        identity: {},
        gender: null,
        birthDate: null,
        familySize: null,
        yearsFarming: null,
        transport: null,
        livelihood: null,
        atTheFarmersLocationOnRegister: null,
        farmerImageUuid: null,
        ethnicity: {
          options: ['Bambukushu', 'Basarwa', 'Bayei','Badxeriku','Batawana','Bakalaka','Banyemba','Baweku','Basubiya'],
          answer: null
        },
        region: {}
      },
    }
  },
  mounted() {
    if (!this.customer) { return; }

    const demographic = this.customer.demographic || {};
    const economic    = this.customer.economic || {};
    // Copies, so the form never changes the stored farmer before Submit.
    const stored      = JSON.parse(JSON.stringify((economic.payload || {}).basicInfo || {}));
    const answers     = ecoBwAnswers(this.customer);
    const fields      = { ...this.basicFormFields, ...stored, ethnicity: { ...this.basicFormFields.ethnicity, answer: answers.ethnicity } };

    fields.familySize       = answers.familySize;
    fields.yearsFarming     = answers.yearsFarming;
    fields.transport        = Array.isArray(answers.transport)  ? [...answers.transport]  : answers.transport;
    fields.livelihood       = Array.isArray(answers.livelihood) ? [...answers.livelihood] : answers.livelihood;
    fields.firstName        = present(this.customer.firstName, stored.firstName);
    fields.surname          = present(this.customer.surname, stored.surname);
    fields.msisdn           = present(this.customer.msisdn);
    fields.identity         = JSON.parse(JSON.stringify(demographic.identity || {}));
    fields.gender           = present(demographic.gender, stored.gender);
    fields.birthDate        = present(demographic.birthdate, stored.birthDate);
    fields.region           = answers.region || {};
    fields.farmerImageUuid  = present(this.customer.imageUrls?.[0], demographic.idScanUrls?.[0]?.url, stored.farmerImageUuid);

    this.basicFormFields = fields;
  },

  computed: {
    complete() { return [] },
    customerUuid() { return this.$route.query.uuid; },
    isStart() { return this.step === 1; },
    isLastStep() { return this.step === 9; },
    msisdn() { return this.customer.msisdn; },
    customer() { return this.$store.getters.customer(this.$route.query.uuid); }
  },

  methods: {

    stepChange(to) {
      this.firstStep = (to=='first');
      this.lastStep = (to=='last');
    },

    isBasicFormFieldsValid() {
      return Object.keys(this.basicFormFields).filter(key => key !== 'atTheFarmersLocationOnRegister').map(key => this.basicFormFields[key]).filter(basic => {return basic === null}).length === 0
          && !!this.basicFormFields.ethnicity.answer
          && !!this.basicFormFields.region
    },

    nextStep() {
      this.$refs.stepper.nextStep();
    },

    previous() { this.step -= 1; },

    toCustomer() { this.$router.push({ name: 'ShowCustomer', query: { uuid: this.customerUuid } }); },
    toFieldSurveyForm() {
      this.$router.push({ name: 'EcoExistBwFieldSurvey', query: { uuid: this.customerUuid } });
    },

    saveBasicInfo() {
      // Built on a copy: the store's farmer only changes through upsertCustomer.
      const customer  = JSON.parse(JSON.stringify(this.customer));
      const { msisdn, identity, ...answers } = this.basicFormFields;
      const basicInfo = JSON.parse(JSON.stringify(answers));
      const { firstName, surname, gender, birthDate, familySize, yearsFarming, transport, livelihood, ethnicity, region } = answers;

      customer.firstName = firstName
      customer.surname   = surname
      customer.name      = `${firstName} ${surname}`
      customer.msisdn    = msisdn
      customer.fields    = customer.fields || []

      const demographic = customer.demographic || {};
      const geographics = [...(demographic.geographicsArray || [])];
      geographics[0] = region.village;
      geographics[1] = region.ward;

      customer.demographic = {
        ...demographic,
        gender,
        birthdate: birthDate,
        identity,
        ethnicity: ethnicity.answer,
        region:    { village: region.village, ward: region.ward, farmLocation: region.farmLocation },
        // Sent with the region: connect/ also applies geographicsArray, and before #9918 applied it last.
        geographicsArray: geographics,
      };

      const economic = customer.economic || {};
      customer.economic = {
        ...economic,
        dependants:   familySize,
        incomePeriod: yearsFarming,
        transport,
        // familySize, yearsFarming and livelihood at the top are what connect/ read before #9918.
        familySize,
        yearsFarming,
        livelihood,
        payload: { ...(economic.payload || {}), livelihood, basicInfo },
      };

      this.save(customer)
    },

    validate() {
      this.$refs.basicInfoForm.validate();
    },
    saveAndDone() {
      this.validate()
      if (this.isBasicFormFieldsValid()) {
        this.saveBasicInfo();
        this.toCustomer();
      }
    },
    saveAndGoToFieldSurvey() {
      this.validate()
      if (this.isBasicFormFieldsValid()) {
        this.saveBasicInfo();
        this.toFieldSurveyForm();
      }
    },
    save(customer) {
      this.snackbar = true;
      this.$store.dispatch('upsertCustomer', customer);
    },

  },

}
</script>
